<template>
  <div class="c-sharing-level-card">
    <label
      v-for="option in visibilityOptions"
      :key="option.value"
      :class="[
        'c-sharing-level-card__option',
        {
          'c-sharing-level-card__option--active': modelValue === option.value,
          'c-sharing-level-card__option--disabled': disabled || option.disabled,
        },
      ]"
    >
      <input
        type="radio"
        :value="option.value"
        :checked="modelValue === option.value"
        :disabled="disabled || option.disabled"
        class="c-sharing-level-card__radio"
        @change="handleOptionClick(option.value)"
      />
      <div class="c-sharing-level-card__content">
        <div class="c-sharing-level-card__header">
          <span class="c-sharing-level-card__title">{{ option.label }}</span>
          <BittsTag
            v-if="'tag' in option && option.tag"
            color="info"
            size="x-small"
          >
            {{ option.tag }}
          </BittsTag>
        </div>
        <span class="c-sharing-level-card__description">{{
          option.description
        }}</span>
      </div>
    </label>
  </div>
</template>

<script setup lang="ts">
import { BittsTag } from '@crossbeam/bitts';

import { computed } from 'vue';

import {
  GREENFIELD_SHARING,
  HIDDEN,
  NEW_SHARING_TYPES_MAP,
  OLAPS_ONLY,
  SHARING,
  type SharingType,
} from '@/constants/data_shares';

type Props = {
  modelValue: SharingType | null;
  disabled?: boolean;
  showGreenfield?: boolean;
  isGreenfieldBlocked?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  showGreenfield: true,
  isGreenfieldBlocked: false,
});

const emit = defineEmits<{
  'update:modelValue': [value: SharingType];
}>();

type OptionWithDisabled = {
  value: SharingType;
  label: string;
  description: string;
  tag?: string;
  disabled?: boolean;
};

const visibilityOptions = computed((): OptionWithDisabled[] => {
  const options: OptionWithDisabled[] = [];

  if (props.showGreenfield) {
    options.push({
      ...NEW_SHARING_TYPES_MAP[GREENFIELD_SHARING],
      disabled: props.isGreenfieldBlocked,
    });
  }

  options.push(
    NEW_SHARING_TYPES_MAP[SHARING],
    NEW_SHARING_TYPES_MAP[OLAPS_ONLY],
    NEW_SHARING_TYPES_MAP[HIDDEN],
  );

  return options;
});

function handleOptionClick(value: SharingType) {
  const option = visibilityOptions.value.find((opt) => opt.value === value);
  if (!props.disabled && !option?.disabled) {
    emit('update:modelValue', value);
  }
}
</script>

<style scoped lang="pcss">
@reference "../../../../css/theme.css";

.c-sharing-level-card {
  @apply flex flex-col gap-8 w-full;
}

.c-sharing-level-card__option {
  @apply flex items-center gap-16 p-16 rounded-[10px] border border-solid border-neutral-border
    bg-white transition-all cursor-pointer shadow-[0px_2px_3px_rgba(13,25,42,0.05)];
}

.c-sharing-level-card__option:hover:not(
    .c-sharing-level-card__option--disabled
  ) {
  @apply border-neutral-border-focus;
}

.c-sharing-level-card__option--active {
  @apply border-secondary-border border-2;
}

.c-sharing-level-card__option--disabled {
  @apply cursor-not-allowed;
}

.c-sharing-level-card__radio {
  @apply flex-shrink-0 w-16 h-16 cursor-pointer appearance-none border border-solid
    border-neutral-border rounded-full relative;
}

.c-sharing-level-card__radio:checked::after {
  content: '';
  @apply absolute inset-0 m-auto w-8 h-8 rounded-full bg-secondary-accent;
}

.c-sharing-level-card__radio:disabled {
  @apply cursor-not-allowed opacity-50;
}

.c-sharing-level-card__content {
  @apply flex flex-col gap-2 flex-1;
}

.c-sharing-level-card__header {
  @apply flex items-center gap-8;
}

.c-sharing-level-card__title {
  @apply text-base font-bold text-neutral-text-strong;
}

.c-sharing-level-card__description {
  @apply text-sm text-neutral-text;
}
</style>
